import { posthogHeaders } from './analytics/client';
import { API_TIMEOUT_MS, ASK_REQUEST_TIMEOUT_MS, CLIENT_TIMEOUT_GRACE_MS } from '../core/timeouts';
export type GetToken = () => Promise<string | null>;
export class ApiError extends Error {
  constructor(public status: number, public reason: string, message: string, public nextAvailableAt?: string) { super(message); }
}
/** An inconclusive subscription lookup is retryable, not a revocation. */
/** Any failure as an `ApiError`; anything that is not one is a request that never finished. */
export const toApiError = (failure: unknown): ApiError => failure instanceof ApiError ? failure : new ApiError(0, 'unreachable', 'We could not finish that request. Try again.');
export const accessRevoked = (reason: string) => reason === 'disabled' || reason === 'subscription_required';
/** The two calls that wait on a long model run. Must match `budgetFor` in the Worker. */
const longRequest = (path: string, method?: string) => method === 'POST' && (path === '/api/asks' || path === '/api/standards/extract');
export async function call<T>(getToken: GetToken, path: string, options: RequestInit = {}): Promise<T> {
  const controller = new AbortController();
  const cancel = () => controller.abort();
  options.signal?.addEventListener('abort', cancel, { once: true });
  if (options.signal?.aborted) cancel();
  const timeoutMs = (longRequest(path, options.method) ? ASK_REQUEST_TIMEOUT_MS : API_TIMEOUT_MS) + CLIENT_TIMEOUT_GRACE_MS;
  let timer: ReturnType<typeof setTimeout>;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      reject(new ApiError(504, 'request_timeout', 'This request took too long. Please try again.'));
      controller.abort();
    }, timeoutMs);
  });
  try {
    return await Promise.race([timeout, (async () => {
      const token = await getToken();
      // Token acquisition cannot be aborted, but a late token must not start another ask.
      controller.signal.throwIfAborted();
      const response = await fetch(path, { ...options, signal: controller.signal, headers: { ...posthogHeaders(), ...(options.body ? { 'Content-Type': 'application/json' } : {}), ...(token ? { Authorization: `Bearer ${token}` } : {}), ...options.headers } });
      const body = (response.status === 204 ? {} : await response.json()) as { error?: string; reason?: string; message?: string; nextAvailableAt?: string };
      if (!response.ok) throw new ApiError(response.status, body.reason ?? body.error ?? 'unavailable', body.message ?? 'We could not finish that request.', body.nextAvailableAt);
      return body as T;
    })()]);
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError(0, 'unreachable', 'We could not connect. Check your connection and try again.');
  } finally {
    clearTimeout(timer!);
    options.signal?.removeEventListener('abort', cancel);
  }
}
export interface Allowance { used: number; limit: number; remaining: number; nextAvailableAt: string | null }
export interface Usage { asks: Allowance; uploads: Allowance }
/** A teacher's saved standards, as `GET /api/standards` returns them. */
export interface SavedStandard { standardId: string; code: string; statement: string; gradeLevels: string[]; subject: string | null; position: number }
export interface StandardSet { format: 'csv' | 'paste' | 'pdf'; rowCount: number; updatedAt: string; rows: SavedStandard[] }
export function safeUrl(value: string): string | undefined {
  try { const url = new URL(value); return ['https:', 'http:'].includes(url.protocol) ? url.href : undefined; } catch { return undefined; }
}
